import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Column, DataTable } from "@/components/ui/data-table";
import type { PartnerServiceListItem } from "@/types/partner-settings";
import { ServiceStatus } from "@/types/partner-settings";
import { cn } from "@/lib/utils";
import { formatServiceSchedule } from "@/components/pages/services/utils/serviceSchedule";
import { formatPartnerServiceLocation } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFormat";
import { toLowerKey } from "@/components/pages/guesteria-partners/utils/guesteriaServicesFilters";
import {
  SERVICES_STATUS_HEADER_KEY,
  servicesStatusValueKey,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesI18nKeys";
import {
  sortServices,
  type ServicesSortState,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesSorting";

export type GuesteriaServicesTableProps = {
  services: PartnerServiceListItem[];
  isLoading?: boolean | undefined;
  sort: ServicesSortState;
  onSortChange: (field: string) => void;
  pagination?:
    | {
        currentPage: number;
        totalPages: number;
        onPageChange: (page: number) => void;
      }
    | undefined;
};

function getStatusBadgeClass(status: ServiceStatus): string {
  switch (status) {
    case ServiceStatus.ACTIVE:
      return "bg-green-50 text-green-700";
    case ServiceStatus.INACTIVE:
      return "bg-gray-50 text-gray-700";
    default:
      return "bg-gray-50 text-gray-700";
  }
}

export const GuesteriaServicesTable = ({
  services,
  isLoading,
  sort,
  onSortChange,
  pagination,
}: GuesteriaServicesTableProps): React.JSX.Element => {
  const { t } = useTranslation();

  const columns: Column<PartnerServiceListItem>[] = useMemo(
    () => [
      {
        key: "name",
        header: t("partners.services.table.name"),
        sortable: true,
        className: "flex-1",
        render: (_v, row) => (
          <div className="truncate">
            <p className="font-medium text-[#171717]">{row.name}</p>
            <p className="text-muted-foreground truncate text-xs">
              {formatPartnerServiceLocation(row.destination)}
            </p>
            <p className="text-muted-foreground line-clamp-2 text-xs">
              {row.description}
            </p>
          </div>
        ),
      },
      {
        key: "price",
        header: t("partners.services.table.price"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) =>
          row.price != null ? (
            <span className="text-sm font-medium text-[#171717]">
              €{row.price.toFixed(2)}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          ),
      },
      {
        key: "rating",
        header: t("partners.services.table.rating"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) =>
          row.rating != null ? (
            <div className="flex items-center justify-center gap-1">
              <Star className="size-3.5 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium text-[#171717]">
                {row.rating.toFixed(1)}
              </span>
              {row.reviewCount != null && (
                <span className="text-muted-foreground text-xs">
                  ({row.reviewCount})
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          ),
      },
      {
        key: "categories",
        header: t("partners.services.table.categories"),
        sortable: true,
        className: "w-[150px]",
        render: (_v, row) =>
          row.categories.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {row.categories.slice(0, 2).map((cat) => (
                <span
                  key={cat.tagId}
                  className="inline-block rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
                >
                  {cat.name}
                </span>
              ))}
              {row.categories.length > 2 && (
                <span className="text-muted-foreground text-xs">
                  +{row.categories.length - 2}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          ),
      },
      {
        key: "schedule",
        header: t("partners.services.table.schedule"),
        sortable: false,
        className: "w-[170px]",
        render: (_v, row) => (
          <span className="text-sm text-[#171717]">
            {formatServiceSchedule(
              row,
              t("services.activity.common.na", "N/A"),
            )}
          </span>
        ),
      },
      {
        key: "status",
        header: t(SERVICES_STATUS_HEADER_KEY),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
              getStatusBadgeClass(row.status),
            )}
          >
            {row.status
              ? t(servicesStatusValueKey(toLowerKey(row.status)))
              : "—"}
          </span>
        ),
      },
      {
        key: "isRecommended",
        header: t("partners.services.table.recommended"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) =>
          row.isRecommended ? (
            <span className="text-sm font-medium text-green-700">
              {t("common.yes")}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">
              {t("common.no")}
            </span>
          ),
      },
    ],
    [t],
  );

  const sortedServices = useMemo(
    () => sortServices(services, sort),
    [services, sort],
  );

  return (
    <DataTable<PartnerServiceListItem>
      data={sortedServices}
      columns={columns}
      getRowKey={(row) => row.id}
      columnHeaderClassName="text-xs font-semibold bg-gray-50"
      sorting={{
        field: sort.field,
        direction: sort.direction,
        onSort: onSortChange,
      }}
      emptyMessage={t("partners.services.table.empty")}
      loading={isLoading}
      pagination={pagination}
    />
  );
};
